"use client";

import { APP_HEADER_HEIGHT, HOME_PAGE } from "@/constants";
import { useFeatureFlag, usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { NotificationProvider } from "@/context/NotificationProvider";
import { useProfileDrawer } from "@/context/ProfileDrawerContext";
import { useGetWalletGems } from "@/hooks/api";
import { isMobile } from "@/utils/browser";
import {
  inFacebookBrowser,
  redirectGameToExternalBrowser,
} from "@/utils/loginredirect";
import { trackLegacyEvent } from "@analytics";
import { AppHeaderBar, Box, Link, Stack } from "@biom3/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useRouter } from "next/navigation";
import { type Ref, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProfileDrawer } from "../ProfileDrawer/ProfileDrawer";
import { UserActivity } from "../UserActivities/UserActivity";
import { HeaderProgressBar } from "../WeeklyRewards/HeaderProgressBar";
import { useWeeklyRewards } from "../WeeklyRewards/WeeklyRewardsProvider";
import { LoadingModal } from "./LoadingModal";
import { SignInBadge } from "./sidemenu/AvatarBadge";
import { HeaderProfilePill } from "./sidemenu/HeaderProfilePill";

export function Header({
  domRef,
  onMenuClick,
  onTitleClick,
}: {
  domRef: Ref<HTMLDivElement> | undefined;
  onMenuClick: () => void;
  onTitleClick: () => void;
}) {
  const flags = useFeatureFlag();
  const { userActivities, predictedKey } = useFlags();
  const { t } = useTranslation();
  const { walletAddress, isLoggedIn, passportState, isLoggingIn } =
    usePassportProvider();
  const [isCopyWalletModalVisible, setCopyWalletModalVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { openLoginModal } = useLoginModal();
  const { setIsOpen: setProfileDrawerOpen } = useProfileDrawer();
  const { walletGems } = useGetWalletGems(walletAddress || null, false);
  const { eligibility } = useWeeklyRewards();

  const router = useRouter();

  const shouldShowHeaderProgressBar = useMemo(
    () =>
      predictedKey &&
      isLoggedIn &&
      eligibility !== undefined &&
      eligibility.is_eligible,
    [predictedKey, isLoggedIn, eligibility],
  );

  const onLogin = () => {
    const inAdBrowser = inFacebookBrowser();
    const redirectEnabled =
      flags["facebook-ad-redirect-enabled"] === "VariantA";
    const extras = inAdBrowser ? { redirectEnabled } : {};
    trackLegacyEvent({
      userJourney: "Activity",
      screen: "Header",
      control: "SignIn",
      controlType: "Button",
      action: "Pressed",
      extras: {
        inAdBrowser: inFacebookBrowser(),
        path: window.location.pathname,
        ...extras,
      },
    });

    // If we are on a games page in an ad browser, we need to redirect to the external browser
    if (!redirectEnabled || !redirectGameToExternalBrowser(false)) {
      openLoginModal(async () => {
        if (isMobile()) await router.replace("/discover");
      });
    }
  };

  const handleProfileClick = () => {
    if (isLoggedIn) {
      setProfileDrawerOpen(true);
    } else {
      onLogin();
    }
  };

  return (
    <>
      <AppHeaderBar
        variant="bright"
        domRef={domRef}
        testId="header"
        size="small"
        sx={{
          background: "base.color.neutral.900",
          height: isMobile() ? "70px" : APP_HEADER_HEIGHT,
          '& [data-testid="header__innerContainer"]': {
            // biome has hardcoded gap: 24px in the header bar, so we need to override it...
            gap: isMobile() ? "12px" : "0",
          },
        }}
      >
        {shouldShowHeaderProgressBar && (
          <AppHeaderBar.Title
            testId="header-progress-bar-title"
            sx={{
              display: "flex",
              justifyContent: "center",
              flex: 1,
              px: { default: "1px", medium: "16px" },
            }}
          >
            <HeaderProgressBar />
          </AppHeaderBar.Title>
        )}
        <AppHeaderBar.LeftLogo
          testId="compact-header-logo"
          logo="PlayHorizontalLockup"
          rc={<Link rc={<a onClick={onTitleClick} href={HOME_PAGE} />} />}
          sx={{
            cursor: "pointer",
          }}
        />
        <AppHeaderBar.LeftButtCon
          testId="header-smallscreen"
          size="small"
          icon="Menu"
          onClick={onMenuClick}
          sx={{ d: { default: "flex", medium: "none" } }}
        />
        <AppHeaderBar.RightSlot>
          <Stack
            direction="row"
            gap="base.spacing.x8"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            {passportState.ready && (
              <Stack
                direction="row"
                alignItems="center"
                gap="base.spacing.x2"
                sx={{ height: "100%" }}
              >
                {isLoggedIn && userActivities && (
                  <UserActivity walletAddress={walletAddress || null} />
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "base.spacing.x2",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={handleProfileClick}
                >
                  {isLoggedIn ? (
                    <>
                      <HeaderProfilePill
                        gems={walletGems?.gems ?? 0}
                        avatarUrl={undefined}
                        onClick={handleProfileClick}
                      />
                      <NotificationProvider>
                        <ProfileDrawer
                          setLoggingOut={setLoggingOut}
                          setCopyWalletModalVisible={setCopyWalletModalVisible}
                          isCopyWalletModalVisible={isCopyWalletModalVisible}
                          showTarget={false}
                        />
                      </NotificationProvider>
                    </>
                  ) : (
                    <SignInBadge
                      sx={{
                        zIndex: 3,
                        fontSize: "20px",
                        px: "20px",
                        py: "8px",
                        borderRadius: "999px",
                      }}
                      onClick={onLogin}
                    />
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </AppHeaderBar.RightSlot>
      </AppHeaderBar>
      <LoadingModal loading={isLoggingIn} text={t("logging_in")} />
      <LoadingModal loading={loggingOut} text={t("logging_out")} />
    </>
  );
}
